import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NbButtonModule, NbPopoverModule, NbDialogService } from "@nebular/theme";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faFileLines } from "@fortawesome/pro-light-svg-icons";
import { combineLatest, Subscription } from "rxjs";
import { filter, first, skip, switchMap } from "rxjs/operators";
import { APIService, KPI } from "../../../../@core/api";
import { DocumentTypesService } from "../../../../@core/utils/document-types.service";
import { DocumentsService } from "../../../../@core/utils";
import { DocumentSheetComponent } from "../../document-sheet/document-sheet.component";
import { AgGridDocumentsComponent } from "../../ag-grid-documents/ag-grid-documents.component";
import { AddDocumentDialogComponent } from "../../ag-grid-documents/add-document-dialog/add-document-dialog.component";
import { BasicDialogComponent } from "../../basic-dialog/basic-dialog.component";
import { RightPanelDialogService } from "../../../../@core/utils/right-panel-dialog.service";

interface AssetDocumentView {
  document_type: KPI.DocumentType | null;
  mandatory: boolean;
  documents: KPI.Document[];
}

@Component({
  selector: "app-documents-tab",
  templateUrl: "./documents-tab.component.html",
  styleUrls: ["./documents-tab.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    NbButtonModule,
    NbPopoverModule,
    TranslateModule,
    FontAwesomeModule
  ]
})
export class DocumentsTabComponent implements OnChanges, OnDestroy {
  @Input() public asset: KPI.MappedAsset;
  @Input() public loading: boolean = false;
  @Output() public requestFullScreen = new EventEmitter<boolean>();
  @Output() public documentsChanged = new EventEmitter<void>();

  public faFileLines = faFileLines;
  public mandatoryDocRequirements: AssetDocumentView[] = [];
  public otherDocRequirements: AssetDocumentView[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private api: APIService,
    private cdr: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private documentTypesService: DocumentTypesService,
    private documentsService: DocumentsService,
    private translate: TranslateService,
    private sideFormLevelService: RightPanelDialogService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["asset"] && this.asset?.id) {
      this.loadDocuments();
      this.subscriptions.push(
        this.documentsService._documentsHaveChanged$.pipe(
          skip(1),
        ).subscribe(() => {
          this.loadDocuments();
          this.documentsChanged.emit();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private loadDocuments(): void {
    this.mandatoryDocRequirements = [];
    this.otherDocRequirements = [];

    combineLatest([
      this.documentTypesService.documentTypes$.pipe(first()),
      this.api.documents_document_type_relationships(
        { app_label: "assets", model: "assettype", object_id: this.asset.asset_type },
        true,
      ),
      this.api.documents_documents({ assets: this.asset.id }),
    ]).pipe(first()).subscribe({
      next: ([allDocTypes, relationships, documents]) => {
        const docTypeMap = new Map(allDocTypes.map((dt) => [dt.id, dt]));

        const views: AssetDocumentView[] = relationships
          .filter((rel) => docTypeMap.has(rel.document_type))
          .map((rel) => {
            const docType = docTypeMap.get(rel.document_type)!;
            const sortedDocs = documents
              .filter((d) => d.type === docType.label)
              .sort((a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime());
            return {
              document_type: docType,
              mandatory: rel.values?.mandatory ?? false,
              documents: sortedDocs,
            };
          });

        const matchedDocIds = new Set(views.flatMap((v) => v.documents.map((d) => d.id)));
        const unmatchedDocs = documents.filter((d) => !matchedDocIds.has(d.id));
        if (unmatchedDocs.length > 0) {
          views.push({
            document_type: null,
            mandatory: false,
            documents: unmatchedDocs.sort((a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime()),
          });
        }

        this.mandatoryDocRequirements = views.filter((v) => v.mandatory);
        this.otherDocRequirements = views.filter((v) => !v.mandatory);
        this.cdr.detectChanges();
      },
    });
  }

  public onOpenDocumentSheet(doc: KPI.Document): void {
    const site = this.asset?.perimeters?.find((p) => !p.level_parent) ?? this.asset?.perimeters?.[0];
    this.sideFormLevelService.open(DocumentSheetComponent, {
      context: { doc, siteId: site?.id ?? null },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      autoFocus: false,
    });
  }

  public onDownloadDocument(doc: KPI.Document): void {
    if (doc.link) {
      window.open(doc.link, "_blank", "noopener");
    }
  }

  public onAttachExistingDocument(): void {
    this.documentsService.clearDocumentsCache();
    const dialogRef = this.sideFormLevelService.open(AgGridDocumentsComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasBackdrop: true,
      dialogClass: 'attach-document-drawer-overlay',
      context: {
        selectionMode: true,
        linkedTypeFilter: 'asset',
        loadAll: true,
        perimeterIds: [],
        investIds: [],
        assetIds: [],
        actionIds: [],
      },
    });
    dialogRef.onClose.pipe(
      filter((selectedDoc) => !!selectedDoc),
    ).subscribe((selectedDoc: any) => {
      const existingLinked: number[] = selectedDoc.linked_equipment || [];
      const assetId = this.asset.id;
      if (existingLinked.includes(assetId)) return;
      this.documentsService.updateDocumentLinkedEquipment(
        selectedDoc.id,
        [...existingLinked, assetId]
      ).subscribe({
        next: () => {
          this.documentsService.clearDocumentsCache();
          this.documentsService.documentsHaveChanged();
        },
      });
    });
  }

  public onAttachNewDocument(): void {
    this.sideFormLevelService.open(AddDocumentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasBackdrop: true,
      dialogClass: 'add-document-panel-overlay',
      context: {
        investIds: [],
        assetIds: [this.asset.id],
        actionIds: [],
        perimeterIds: [],
        perimeterLabel: '',
        linkedToLabel: this.asset.label || '',
        siteId: null,
        initialLabel: '',
      },
    });
  }

  public onDeleteDocument(event: Event, doc: KPI.Document): void {
    event.stopPropagation();
    const dialog = this.dialogService.open(BasicDialogComponent, {
      autoFocus: false,
      context: {
        title: this.translate.instant('Document deletion'),
        content: this.translate.instant('Do you confirm that {elementName} will be deleted ?', { elementName: doc.title }),
      },
    });
    dialog.onClose.pipe(
      filter((confirmed) => !!confirmed),
      switchMap(() => {
        const assetId = this.asset.id;
        const remaining = (doc.linked_equipment as number[]).filter((id) => id !== assetId);
        if (remaining.length === 0) {
          return this.documentsService.deleteDocument(doc.id);
        }
        return this.documentsService.updateDocumentLinkedEquipment(doc.id, remaining);
      }),
    ).subscribe({
      next: () => {
        this.documentsService.clearDocumentsCache();
        this.documentsService.documentsHaveChanged();
      },
    });
  }
}
